import React, { ReactElement } from "react";

type PolicyFolderArtProps = {
  /**
   * Which layer of the folder to draw. Each keeps the canvas of the picture it replaces, so the
   * opening animation in PolicyEnactedAlert.css lines up exactly:
   *  - "back": the back panel with its tab (642 x 419, panel on the right half).
   *  - "coverBack": the inside of the cover, seen once it has opened (595 x 419, left half).
   *  - "coverFront": the outside of the cover (595 x 419, right half).
   */
  part: "back" | "coverBack" | "coverFront";
  id?: string;
  className?: string;
};

const NAVY_TOP = "#2b4560";
const NAVY_BOTTOM = "#16273a";
const GOLD = "#f0be50";
const GOLD_DARK = "#b9862a";

/** A dossier-style policy folder in the game's navy and gold, drawn as vector art. */
export default function PolicyFolderArt(
  props: PolicyFolderArtProps,
): ReactElement {
  const common = {
    id: props.id,
    className:
      "policy-folder-art" + (props.className ? " " + props.className : ""),
    "aria-hidden": true,
  };

  if (props.part === "back") {
    return (
      <svg {...common} viewBox="0 0 642 419" width="642" height="419">
        <defs>
          <linearGradient id="pf-back" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#223950" />
            <stop offset="1" stopColor="#111e2c" />
          </linearGradient>
        </defs>
        {/* tab */}
        <path
          d="M600 8h26a12 12 0 0 1 12 12v116a12 12 0 0 1-6 10l-26 16z"
          fill={GOLD_DARK}
        />
        <path d="M604 20h20v112l-20 12z" fill={GOLD} opacity="0.55" />
        {/* panel */}
        <path
          d="M298 0h302a12 12 0 0 1 12 12v395a12 12 0 0 1-12 12H298z"
          fill="url(#pf-back)"
        />
        <path
          d="M298 0h302a12 12 0 0 1 12 12v395a12 12 0 0 1-12 12H298"
          fill="none"
          stroke="#4a6580"
          strokeWidth="3"
        />
      </svg>
    );
  }

  if (props.part === "coverBack") {
    // The inside of the cover: a memo held by a gold clip.
    return (
      <svg {...common} viewBox="0 0 595 419" width="595" height="419">
        <defs>
          <linearGradient id="pf-inside" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#2a425b" />
            <stop offset="1" stopColor="#1a2b3d" />
          </linearGradient>
        </defs>
        <path
          d="M0 12A12 12 0 0 1 12 0h285v419H12a12 12 0 0 1-12-12z"
          fill="url(#pf-inside)"
        />
        <path d="M290 0v419" stroke="#0f1a26" strokeWidth="10" opacity="0.6" />
        {/* memo */}
        <rect x="34" y="34" width="226" height="300" rx="6" fill="#f6efe0" />
        <rect
          x="34"
          y="34"
          width="226"
          height="300"
          rx="6"
          fill="none"
          stroke="#d9cdb4"
          strokeWidth="2"
        />
        <g fill="#b9b0a0">
          <rect x="56" y="70" width="96" height="9" rx="4" />
          <rect x="56" y="106" width="182" height="7" rx="3.5" />
          <rect x="56" y="124" width="170" height="7" rx="3.5" />
          <rect x="56" y="142" width="182" height="7" rx="3.5" />
          <rect x="56" y="160" width="120" height="7" rx="3.5" />
          <rect x="56" y="206" width="160" height="7" rx="3.5" />
          <rect x="56" y="224" width="182" height="7" rx="3.5" />
          <rect x="56" y="242" width="96" height="7" rx="3.5" />
        </g>
        {/* clip */}
        <rect x="112" y="18" width="70" height="30" rx="8" fill={GOLD_DARK} />
        <rect x="120" y="22" width="54" height="18" rx="5" fill={GOLD} />
        {/* corner protectors */}
        <path d="M0 12A12 12 0 0 1 12 0h40L0 52z" fill={GOLD_DARK} />
        <path d="M0 407a12 12 0 0 0 12 12h40L0 367z" fill={GOLD_DARK} />
      </svg>
    );
  }

  // The outside of the cover.
  return (
    <svg {...common} viewBox="0 0 595 419" width="595" height="419">
      <defs>
        <linearGradient id="pf-cover" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor={NAVY_TOP} />
          <stop offset="1" stopColor={NAVY_BOTTOM} />
        </linearGradient>
        <radialGradient id="pf-cover-glow" cx="0.5" cy="0.38" r="0.6">
          <stop offset="0" stopColor="#5f8db8" stopOpacity="0.35" />
          <stop offset="1" stopColor="#5f8db8" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="pf-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffd97f" />
          <stop offset="1" stopColor="#d9a136" />
        </linearGradient>
      </defs>
      <path
        d="M298 0h285a12 12 0 0 1 12 12v395a12 12 0 0 1-12 12H298z"
        fill="url(#pf-cover)"
      />
      <path
        d="M298 0h285a12 12 0 0 1 12 12v395a12 12 0 0 1-12 12H298z"
        fill="url(#pf-cover-glow)"
      />
      {/* spine shadow and soft sheen */}
      <rect
        x="298"
        y="0"
        width="14"
        height="419"
        fill="#0b141e"
        opacity="0.45"
      />
      <path d="M330 419L595 150v90L420 419z" fill="#fff" opacity="0.04" />
      {/* stitched gold frame */}
      <rect
        x="326"
        y="22"
        width="247"
        height="375"
        rx="8"
        fill="none"
        stroke={GOLD}
        strokeWidth="2"
        opacity="0.8"
      />
      <rect
        x="334"
        y="30"
        width="231"
        height="359"
        rx="5"
        fill="none"
        stroke={GOLD}
        strokeWidth="1.5"
        strokeDasharray="6 5"
        opacity="0.55"
      />
      {/* seal */}
      <circle
        cx="449.5"
        cy="128"
        r="46"
        fill="none"
        stroke="url(#pf-gold)"
        strokeWidth="4"
      />
      <circle
        cx="449.5"
        cy="128"
        r="37"
        fill="none"
        stroke={GOLD}
        strokeWidth="1.5"
        opacity="0.6"
      />
      <path
        d="M449.5 98l8.2 16.6 18.3 2.7-13.2 12.9 3.1 18.2-16.4-8.6-16.4 8.6 3.1-18.2-13.2-12.9 18.3-2.7z"
        fill="url(#pf-gold)"
      />
      {/* title */}
      <text
        x="449.5"
        y="222"
        textAnchor="middle"
        fontFamily="Montserrat, sans-serif"
        fontWeight="700"
        fontSize="17"
        letterSpacing="7"
        fill={GOLD}
        opacity="0.85"
      >
        NEW
      </text>
      <text
        x="449.5"
        y="272"
        textAnchor="middle"
        fontFamily="Germania One, cursive"
        fontSize="54"
        letterSpacing="3"
        fill="url(#pf-gold)"
      >
        POLICY
      </text>
      <rect x="389" y="288" width="121" height="2" fill={GOLD} opacity="0.6" />
      <text
        x="449.5"
        y="314"
        textAnchor="middle"
        fontFamily="Montserrat, sans-serif"
        fontWeight="600"
        fontSize="11"
        letterSpacing="3"
        fill="#c9d6e2"
        opacity="0.75"
      >
        BY ORDER OF THE
      </text>
      <text
        x="449.5"
        y="331"
        textAnchor="middle"
        fontFamily="Montserrat, sans-serif"
        fontWeight="600"
        fontSize="11"
        letterSpacing="3"
        fill="#c9d6e2"
        opacity="0.75"
      >
        GOVERNMENT
      </text>
      {/* stamp */}
      <g transform="rotate(-7 449.5 368)" opacity="0.9">
        <rect
          x="381"
          y="350"
          width="137"
          height="38"
          rx="6"
          fill="none"
          stroke="#e0573f"
          strokeWidth="3.5"
        />
        <text
          x="449.5"
          y="377"
          textAnchor="middle"
          fontFamily="Germania One, cursive"
          fontSize="23"
          letterSpacing="3"
          fill="#e0573f"
        >
          CLASSIFIED
        </text>
      </g>
      {/* corner protectors */}
      <path d="M543 0h40a12 12 0 0 1 12 12v40z" fill={GOLD_DARK} />
      <path d="M543 419h40a12 12 0 0 0 12-12v-40z" fill={GOLD_DARK} />
    </svg>
  );
}
